import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  InputGroup,
  Pagination,
} from 'react-bootstrap';
import IconAlert from '@/components/common/IconAlert';
import SubtleBadge from '@/components/common/SubtleBadge';
import { indexerAlertVariant } from '../lib/indexer-alert-variant';
import { useSearchParams } from 'react-router-dom';
import { getIndexerErrorInfo } from '../api/errors';
import { useLazyListUrlStatusChangesQuery } from '../api/indexerApi';
import type { UrlChangeFilter, UrlStatusChangeRow } from '../api/types';
import { NeedProject } from '../components/NeedProject';
import { IndexerIcon } from '../components/IndexerIcon';
import {
  IndexerKpiCard,
  type IndexerKpiTone,
} from '../components/IndexerKpiCard';
import {
  IndexerTablePreload,
  UrlChangesPagePreload,
} from '../components/preloads/IndexerPreloads';
import { UrlStatusChangeLog } from '../components/UrlStatusChangeLog';
import {
  parseStatsDaysParam,
  parseUrlChangeFilter,
} from '../lib/drilldown-links';
import { useProject } from '../lib/project-context';
import { statsRangeHint, type StatsRange } from '../lib/job-range';
import { formatFaNumber } from '../lib/utils';

const PAGE_SIZE = 30;

type Summary = {
  total: number;
  becameIndexed: number;
  becameNotIndexed: number;
  error: number;
  other: number;
};

const EMPTY_SUMMARY: Summary = {
  total: 0,
  becameIndexed: 0,
  becameNotIndexed: 0,
  error: 0,
  other: 0,
};

const FILTERS: {
  key: UrlChangeFilter;
  label: string;
  tone: IndexerKpiTone;
  summaryKey: keyof Summary;
}[] = [
  { key: 'all', label: 'همه', tone: 'neutral', summaryKey: 'total' },
  {
    key: 'became_indexed',
    label: 'ایندکس شد',
    tone: 'success',
    summaryKey: 'becameIndexed',
  },
  {
    key: 'became_not_indexed',
    label: 'ایندکس از دست رفت',
    tone: 'warning',
    summaryKey: 'becameNotIndexed',
  },
  { key: 'error', label: 'خطا', tone: 'danger', summaryKey: 'error' },
  { key: 'other', label: 'سایر', tone: 'neutral', summaryKey: 'other' },
];

export function UrlChangesPage() {
  const { project, projectRef } = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<UrlStatusChangeRow[]>([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const filter = parseUrlChangeFilter(searchParams.get('filter'));
  const days = parseStatsDaysParam(searchParams.get('days'));
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ variant: string; text: string } | null>(
    null,
  );
  const [listUrlStatusChanges] = useLazyListUrlStatusChangesQuery();

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasQuery = Boolean(search) || filter !== 'all' || Boolean(days);

  const setFilter = useCallback(
    (next: UrlChangeFilter) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === 'all') params.delete('filter');
          else params.set('filter', next);
          return params;
        },
        { replace: true },
      );
      setPage(1);
    },
    [setSearchParams],
  );

  const clearDays = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('days');
        return params;
      },
      { replace: true },
    );
    setPage(1);
  }, [setSearchParams]);

  const submitSearch = useCallback(() => {
    setSearch(searchInput.trim());
    setPage(1);
  }, [searchInput]);

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  }, []);

  const load = useCallback(async () => {
    if (!project || !projectRef) return;
    setLoading(true);
    setMessage(null);
    try {
      const data = await listUrlStatusChanges(
        {
          projectId: projectRef,
          filter: filter === 'all' ? undefined : filter,
          q: search || undefined,
          days,
          page,
          pageSize: PAGE_SIZE,
        },
        true,
      ).unwrap();
      setItems(data.items);
      setSummary(data.summary);
      setTotal(data.total);
    } catch (err) {
      setMessage({
        variant: 'danger',
        text:
          getIndexerErrorInfo(err).message ||
          'بارگذاری لاگ تغییرات ناموفق بود',
      });
    } finally {
      setLoading(false);
    }
  }, [project, projectRef, filter, search, days, page, listUrlStatusChanges]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [filter, search, days]);

  const filterCounts = useMemo(() => summary, [summary]);

  if (!project || !projectRef) return <NeedProject />;

  if (loading && items.length === 0) {
    return <UrlChangesPagePreload />;
  }

  return (
    <div className="d-flex flex-column gap-3">
      {message && (
        <IconAlert
          variant={indexerAlertVariant(message.variant)}
          dismissible
          onClose={() => setMessage(null)}
        >
          <p className="mb-0 text-justify pe-3">{message.text}</p>
        </IconAlert>
      )}

      {days && (
        <div>
          <SubtleBadge
            bg="secondary"
            pill
            className="d-inline-flex align-items-center gap-2 fs--1"
          >
            بازه: {statsRangeHint(days as StatsRange)}
            <button
              type="button"
              className="btn-close"
              style={{ fontSize: '0.55rem' }}
              aria-label="حذف فیلتر بازه"
              onClick={clearDays}
            />
          </SubtleBadge>
        </div>
      )}

      <div className="overview-kpi-grid overview-kpi-grid--5">
        {FILTERS.map((item, index) => {
          const count = filterCounts[item.summaryKey];
          const active = filter === item.key;
          return (
            <IndexerKpiCard
              key={item.key}
              tone={item.tone}
              imageIndex={index}
              className={`overview-kpi-card--clickable${
                active ? ' overview-kpi-card--selected' : ''
              }`}
              role="button"
              tabIndex={0}
              onClick={() => setFilter(item.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setFilter(item.key);
                }
              }}
            >
              <div className="overview-kpi-value">
                {formatFaNumber(count)}
              </div>
              <div className="text-secondary small">{item.label}</div>
            </IndexerKpiCard>
          );
        })}
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <InputGroup size="sm" style={{ maxWidth: 320, minWidth: 180 }}>
          <InputGroup.Text>
            <IndexerIcon name="search" />
          </InputGroup.Text>
          <Form.Control
            placeholder="جستجو در آدرس ..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitSearch();
            }}
            aria-label="جستجو در آدرس ..."
          />
          {(searchInput || search) && (
            <Button
              variant="outline-secondary"
              className="fs--2"
              onClick={clearSearch}
              aria-label="پاک کردن جستجو"
            >
              پاک
            </Button>
          )}
          <Button
            variant="falcon-primary"
            className="fs--2"
            onClick={submitSearch}
          >
            جستجو
          </Button>
        </InputGroup>

        <span className="text-secondary small d-inline-flex align-items-center">
          <IndexerIcon name="clock-history" className="me-1" />
          جدیدترین تغییر بالا
        </span>
      </div>

      {loading ? (
        <IndexerTablePreload rows={8} cols={3} />
      ) : (
        <UrlStatusChangeLog
          projectId={projectRef}
          items={items}
          filtered={hasQuery}
        />
      )}

      {totalPages > 1 && !loading && (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span className="text-secondary small">
            {formatFaNumber(total)} تغییر · صفحه {formatFaNumber(page)} از{' '}
            {formatFaNumber(totalPages)}
          </span>
          <Pagination size="sm" className="mb-0">
            <Pagination.Prev
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            />
            <Pagination.Item active>{page}</Pagination.Item>
            <Pagination.Next
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
}
